// Slice 7: left column of the Manufacturing → Plants screen. Stacks
// the Plants list and the unified Dealers-attached box so the right
// column can dedicate its full width to the slice-5 Dealer locations
// slider.
//
// Slice 9: the standalone amber Unattached button is gone — that
// alert is now the 4th tab inside the dealers-attached slider (with
// tone="warning" so it stays amber even while inactive). When no
// plant is selected, the slider auto-switches to the Unattached tab
// AND the effective selection passed downstream is
// UNATTACHED_PLANT_SENTINEL so the hook returns the unassigned bucket.
//
// The column also owns:
//   - the dealers-attached slider mode state (All | App-Using |
//     Non-App | Unattached)
//   - the plant delete-confirm modal (so the plants list stays
//     presentational and the panel doesn't need to know about modals)
//   - the selection ↔ mode synchronization
import { useEffect, useState } from "react";
import type { MfgPlant } from "@/lib/manufacturingClient";
import {
  UNATTACHED_PLANT_SENTINEL,
  isRealPlantSelection,
  isUnattachedSelection,
  type PlantSelection,
} from "./plantDealersDrafts";
import type {
  DealerRosterCounts,
  SubscriberLocationPlantCounts,
} from "./plantDealersFilter";
import {
  ManufacturingPlantDealersAttachedBox,
  type DealersAttachedMode,
} from "./ManufacturingPlantDealersAttachedBox";
import { ManufacturingPlantDeleteConfirm } from "./ManufacturingPlantDeleteConfirm";
import { ManufacturingPlantsList } from "./ManufacturingPlantsList";
import type { useManufacturingPlantsTab } from "./useManufacturingPlantsTab";
import type { UseManufacturingPlantDealersTab } from "./useManufacturingPlantDealersTab";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export function ManufacturingPlantsColumn({
  plantsTab,
  dealersTab,
  selection,
  onSelect,
  rosterCounts,
  subscriberLocationCounts,
  activePlantLabel,
  onRequestNewPlant,
  onRequestRefresh,
  refreshBusy = false,
  gateEnabled = true,
  gateLoading = false,
}: {
  plantsTab: ReturnType<typeof useManufacturingPlantsTab>;
  dealersTab: UseManufacturingPlantDealersTab;
  selection: PlantSelection;
  onSelect: (next: PlantSelection) => void;
  rosterCounts: DealerRosterCounts;
  subscriberLocationCounts?: SubscriberLocationPlantCounts;
  activePlantLabel: string;
  /** Slice 8: + New plant / Refresh buttons live inside the Plants
   *  SectionCard header (next to the `?` help). The column is a
   *  passthrough — the panel still owns the gate-refresh and
   *  nav-tick side effects. */
  onRequestNewPlant?: () => void;
  onRequestRefresh?: () => void;
  refreshBusy?: boolean;
  gateEnabled?: boolean;
  gateLoading?: boolean;
}) {
  const [mode, setMode] = useState<DealersAttachedMode>("unattached");

  // Slice 9: keep the slider mode in sync with the selection.
  //   - selection === null OR sentinel → mode = "unattached"
  //   - selection === real plant id AND mode was "unattached"
  //     → bump mode to "all" so the user lands on the most
  //     informative view of that plant's roster.
  // Other selection↔mode transitions keep whatever mode the user
  // last picked (e.g. switching plants while on App-Using stays on
  // App-Using for the new plant).
  useEffect(() => {
    if (selection === null || isUnattachedSelection(selection)) {
      setMode("unattached");
    } else if (mode === "unattached") {
      setMode("all");
    }
  }, [selection, mode]);

  function handleModeChange(next: DealersAttachedMode) {
    if (next === "unattached") {
      setMode(next);
      onSelect(UNATTACHED_PLANT_SENTINEL);
      return;
    }
    // Picking a non-unattached tab while no real plant is selected —
    // jump to the default plant so the body has something to show
    // (the hook returns [] when selection is null/sentinel for the
    // attached views). The mode setter follows the selection update
    // via the useEffect above.
    if (!isRealPlantSelection(selection)) {
      const fallback =
        plantsTab.plants.find((p) => p.is_default) ?? plantsTab.plants[0];
      if (fallback) {
        setMode(next);
        onSelect(fallback.id);
        return;
      }
      // No plants exist yet — keep the user on the unattached tab so
      // they don't end up staring at an undifferentiated empty body.
      return;
    }
    setMode(next);
  }

  // Delete-confirm modal state. Held here so the list component is
  // pure and the panel doesn't need to know. `error` retains the last
  // backend refusal (e.g. FK-blocked) so the user sees why the click
  // didn't take effect.
  const [pendingDelete, setPendingDelete] = useState<MfgPlant | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (pendingDelete) setDeleteError(null);
  }, [pendingDelete]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    const res = await plantsTab.deletePlant(pendingDelete);
    if (res.ok) {
      if (selection === pendingDelete.id) onSelect(null);
      setPendingDelete(null);
      setDeleteError(null);
      return;
    }
    setDeleteError(res.error);
  }

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "mfg-plant-detail" }} className="space-y-3">
      <ManufacturingPlantsList
        m={plantsTab}
        selection={selection}
        onSelect={onSelect}
        rosterCounts={rosterCounts}
        subscriberLocationCounts={subscriberLocationCounts}
        onRequestDelete={(plant) => setPendingDelete(plant)}
        onRequestNewPlant={onRequestNewPlant}
        onRequestRefresh={onRequestRefresh}
        refreshBusy={refreshBusy}
        gateEnabled={gateEnabled}
        gateLoading={gateLoading}
      />

      {isRealPlantSelection(selection) || selection === UNATTACHED_PLANT_SENTINEL || selection === null ? (
        <ManufacturingPlantDealersAttachedBox
          m={dealersTab}
          activePlantLabel={activePlantLabel}
          mode={mode}
          onModeChange={handleModeChange}
          unattachedCount={rosterCounts.unattached}
        />
      ) : null}

      <ManufacturingPlantDeleteConfirm
        open={pendingDelete !== null}
        plant={pendingDelete}
        busy={plantsTab.actionBusy}
        error={deleteError}
        onCancel={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
